// CANVAS VIEWPORT CHROME (RS lane, owner directive 2026-07-21): overlay
// scrollbars + rulers around the fabric work surface.
//
// STRUCTURE — this component OWNS the viewport wrapper. The fabric container
// (.cce-canvas-container, the div the editor hook sizes the canvas to via its
// ResizeObserver) is absolutely positioned inside it; the ruler strips sit in
// a 22px top/left gutter OUTSIDE that container, so:
//   · toggling rulers shifts the container by 22px → the hook's own
//     ResizeObserver refits (fit mode) or re-measures + clamps (manual mode)
//     — a SETTING change, allowed to move the poster;
//   · drawer overlays (DL lane) never touch this wrapper, so they still
//     never move anything.
//
// PAN PATH — thumb drags / track jumps go through editor.panBy (the same
// relativePan + manualViewportRef + clampViewport recipe as space-drag), so
// the fit-mode trap is respected: a scrollbar pan disarms the ResizeObserver
// refit exactly like every other manual pan, and the vpt change funnels
// through a canvas render → the after:render vpt-compare → the fx
// viewport-patch debounce. No new render loops, no synchronous re-bakes.
//
// SYNC — one rAF-throttled sync() redraws thumb geometry + both ruler strips.
// It is driven by the SAME signal the patch system trusts: after:render with
// a change key (vpt + canvas dims + workspace rect), plus a ResizeObserver on
// the wrapper (strip lengths) and pref changes. No polling.
//
// UNITS — 72 canvas units = 1 inch (print300 contract). Ruler origin: 0 at
// the poster's top-left corner, negative into the margin band.

import React, { useCallback, useEffect, useRef } from 'react';
import { useCanvasPrefs } from './canvasPrefs';
import './canvasViewport.css';

const RULER = 22;            // strip thickness (px) — must match canvasViewport.css
const MIN_THUMB = 28;        // px — smallest draggable thumb
const IDLE_FADE_MS = 900;    // bars fade this long after the last vpt change

// Per-unit tick tables (steps are in the UNIT; perUnit converts to canvas
// units — 72u = 1in). minorDiv = minor ticks per label step; midEvery =
// every Nth minor is a taller mid tick (inches: halves; cm: 5mm; px: none).
// labelMin = smallest on-screen label gap before the ladder walks up a step
// (per-unit so the directive's base cases hold at 100%: inches label each
// inch, cm each cm, px each 50).
//   in: ticks 1in/½/¼, labels each inch
//   cm: ticks 1cm/1mm (5mm mid), labels each cm
//   px: raw canvas units, ticks 50/10
const UNIT_DEFS = {
  in: { perUnit: 72, ladder: [0.25, 0.5, 1, 2, 5, 10, 25, 50], minorDiv: 4, midEvery: 2, labelMin: 44 },
  cm: { perUnit: 72 / 2.54, ladder: [0.5, 1, 2, 5, 10, 25, 50, 100], minorDiv: 10, midEvery: 5, labelMin: 26 },
  px: { perUnit: 1, ladder: [10, 25, 50, 100, 250, 500, 1000, 2500], minorDiv: 5, midEvery: null, labelMin: 44 },
};

// Brightivity kit (light): white rulers, navy ticks, blue cursor.
const INK = {
  bg: '#FFFFFF',
  edge: 'rgba(27, 37, 80, 0.12)',
  tick: 'rgba(84, 90, 120, 0.55)',
  tickMinor: 'rgba(84, 90, 120, 0.28)',
  label: '#545A78',
  cursor: 'rgba(26, 176, 242, 0.9)',
};

const fmtLabel = (v) => String(Math.round(v * 100) / 100);

// ---- ruler guides (RS2) ----
const GUIDE_HALF_SLAB = 3; // 7px invisible hit slab, hairline at +3
const SNAP_FLASH_MS = 260; // guide stays highlighted this long after the last snap tick
// Granular-measurement decimals per unit (owner: "granular measurements")
const GUIDE_LABEL_DECIMALS = { in: 2, cm: 1, px: 0 };

/** One source of truth with the ruler ticks: same UNIT_DEFS.perUnit tables,
 *  same origin (0 at the poster corner) — the pill shows exactly the number
 *  the ruler reads at that line. */
function fmtGuideLabel(posCanvasUnits, units) {
  const def = UNIT_DEFS[units] || UNIT_DEFS.in;
  const d = GUIDE_LABEL_DECIMALS[units] ?? 2;
  return `${(posCanvasUnits / def.perUnit).toFixed(d)} ${UNIT_DEFS[units] ? units : 'in'}`;
}

/** Reachable view-start range along one axis, in "doc px" (screen px at the
 *  current zoom, origin = the poster's start edge). The margin band — half a
 *  viewport past each poster edge — is INTERSECTED with what clampViewport
 *  permits (its quarter-poster-on-screen law), so the thumb never promises
 *  travel the clamp would immediately take back. */
function axisMetrics(viewSize, posterStartScr, posterSizeScr) {
  const viewStartDoc = -posterStartScr;
  const fits = posterStartScr >= -0.5 && posterStartScr + posterSizeScr <= viewSize + 0.5;
  const lo = Math.max(-viewSize / 2, 0.25 * posterSizeScr - viewSize);
  const hi = Math.max(lo, Math.min(posterSizeScr - viewSize / 2, 0.75 * posterSizeScr));
  return { fits, lo, hi, viewStartDoc };
}

/** Redraw one ruler strip. DPR-aware: the backing store is css × dpr and the
 *  context is scaled, so 1px ticks stay crisp on the DPR-2 school PCs. */
function drawRulerStrip(el, { horizontal, cssLen, dpr, zoom, vptOffset, posterOrigin, unitDef, cursorPx }) {
  if (!el || cssLen <= 0) return;
  const wantW = Math.max(1, Math.round((horizontal ? cssLen : RULER) * dpr));
  const wantH = Math.max(1, Math.round((horizontal ? RULER : cssLen) * dpr));
  if (el.width !== wantW) el.width = wantW;
  if (el.height !== wantH) el.height = wantH;
  const ctx = el.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const w = horizontal ? cssLen : RULER;
  const h = horizontal ? RULER : cssLen;
  ctx.fillStyle = INK.bg;
  ctx.fillRect(0, 0, w, h);
  // Inner hairline drawn (not a CSS border) so the 22px box stays exact.
  ctx.fillStyle = INK.edge;
  if (horizontal) ctx.fillRect(0, RULER - 1, w, 1);
  else ctx.fillRect(RULER - 1, 0, 1, h);

  const { perUnit, ladder, minorDiv, midEvery, labelMin } = unitDef;
  const unitPx = perUnit * zoom;
  let labelStep = ladder[ladder.length - 1];
  for (let i = 0; i < ladder.length; i++) {
    if (ladder[i] * unitPx >= labelMin) { labelStep = ladder[i]; break; }
  }
  let minorStep = labelStep / minorDiv;
  const minorPx = minorStep * unitPx;
  const drawMinor = minorPx >= 3.5;
  const drawMid = midEvery != null && minorPx * midEvery >= 3.5;

  // strip px ↔ unit value (origin law: 0 at the poster corner)
  const pxToVal = (px) => ((px - vptOffset) / zoom - posterOrigin) / perUnit;
  const valToPx = (val) => (posterOrigin + val * perUnit) * zoom + vptOffset;

  const v0 = pxToVal(-1);
  const v1 = pxToVal(cssLen + 1);
  // Safety valve — never iterate unbounded on a degenerate zoom.
  while ((v1 - v0) / minorStep > 2000) minorStep *= 2;

  ctx.font = '9px system-ui, sans-serif';
  const kStart = Math.floor(v0 / minorStep);
  const kEnd = Math.ceil(v1 / minorStep);
  const perLabel = Math.max(1, Math.round(labelStep / minorStep));
  for (let k = kStart; k <= kEnd; k++) {
    const isLabel = k % perLabel === 0;
    const isMid = !isLabel && midEvery != null && k % midEvery === 0;
    if (!isLabel && !(isMid ? drawMid : drawMinor)) continue;
    const val = k * minorStep;
    const px = Math.round(valToPx(val));
    if (px < -1 || px > cssLen + 1) continue;
    const len = isLabel ? 8 : isMid ? 6 : 4;
    ctx.fillStyle = isLabel ? INK.tick : INK.tickMinor;
    if (horizontal) ctx.fillRect(px, RULER - 1 - len, 1, len);
    else ctx.fillRect(RULER - 1 - len, px, len, 1);
    if (isLabel) {
      ctx.fillStyle = INK.label;
      if (horizontal) {
        ctx.fillText(fmtLabel(val), px + 3, 9);
      } else {
        ctx.save();
        ctx.translate(13, px - 3);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(fmtLabel(val), 0, 0);
        ctx.restore();
      }
    }
  }

  // Faint cursor position marker (pointermove on the wrapper — no render loop)
  if (cursorPx != null && cursorPx >= 0 && cursorPx <= cssLen) {
    ctx.fillStyle = INK.cursor;
    const p = Math.round(cursorPx);
    if (horizontal) ctx.fillRect(p, 0, 1, RULER - 1);
    else ctx.fillRect(0, p, RULER - 1, 1);
  }
}

export default function CanvasViewport({ editor, containerRef, onDragOver, onDrop, children }) {
  const prefs = useCanvasPrefs();
  const wrapRef = useRef(null);
  const hRulerRef = useRef(null);
  const vRulerRef = useRef(null);
  const hBarRef = useRef(null);
  const hThumbRef = useRef(null);
  const vBarRef = useRef(null);
  const vThumbRef = useRef(null);

  // Latest facade/prefs without resubscribing (the facade identity churns
  // per render; the CANVAS instance is the stable dependency).
  const editorRef = useRef(null);
  editorRef.current = editor;
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const cursorRef = useRef(null); // {x,y} in fabric-canvas element px
  const guidesLayerRef = useRef(null); // DOM guide hairlines (never fabric objects)
  const labelRef = useRef(null);       // the position pill
  const escHandlerRef = useRef(null);  // armed only while a guide gesture runs
  const stateRef = useRef({
    raf: 0, idleTimer: 0, drag: null, bars: { h: null, v: null },
    // guide gesture: { mode:'create'|'move', axis, index, pos, origPos,
    //                  valid, overRuler, pointerId }
    gesture: null,
    snapTimer: 0,
  });

  const fabricCanvas = editor?.canvas || null;

  const markActivity = useCallback(() => {
    const st = stateRef.current;
    if (hBarRef.current) hBarRef.current.classList.add('is-live');
    if (vBarRef.current) vBarRef.current.classList.add('is-live');
    if (st.idleTimer) clearTimeout(st.idleTimer);
    st.idleTimer = setTimeout(() => {
      st.idleTimer = 0;
      if (st.drag) return; // pointerup re-arms the fade
      if (hBarRef.current) hBarRef.current.classList.remove('is-live');
      if (vBarRef.current) vBarRef.current.classList.remove('is-live');
    }, IDLE_FADE_MS);
  }, []);

  // ---- the one sync: scrollbar geometry + ruler strips (rAF-throttled) ----
  const scheduleSync = useCallback(() => {
    const st = stateRef.current;
    if (st.raf) return;
    st.raf = requestAnimationFrame(() => {
      st.raf = 0;
      const canvas = editorRef.current?.canvas;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const vpt = canvas.viewportTransform;
      const ws = canvas.getObjects?.().find((o) => o.name === 'clip');
      if (!vpt || !ws) return;
      const zoom = canvas.getZoom();
      const cw = canvas.width || 0;
      const ch = canvas.height || 0;
      let br;
      try { br = ws.getBoundingRect(true, true); } catch (_) { return; }

      // ---- scrollbars (screen-px math; hidden when the poster axis fits) ----
      const syncBar = (axis) => {
        const horizontal = axis === 'h';
        const barEl = horizontal ? hBarRef.current : vBarRef.current;
        const thumbEl = horizontal ? hThumbRef.current : vThumbRef.current;
        st.bars[axis] = null;
        if (!barEl || !thumbEl) return;
        const viewSize = horizontal ? cw : ch;
        const posterStartScr = (horizontal ? br.left : br.top) * zoom + vpt[horizontal ? 4 : 5];
        const posterSizeScr = (horizontal ? br.width : br.height) * zoom;
        const m = axisMetrics(viewSize, posterStartScr, posterSizeScr);
        if (m.fits || viewSize <= 0) {
          barEl.classList.add('is-hidden');
          return;
        }
        barEl.classList.remove('is-hidden');
        const trackLen = horizontal ? barEl.clientWidth : barEl.clientHeight;
        const travel = m.hi - m.lo;
        const content = travel + viewSize;
        const thumbLen = Math.min(trackLen, Math.max(MIN_THUMB, content > 0 ? (trackLen * viewSize) / content : trackLen));
        const room = Math.max(0, trackLen - thumbLen);
        let pos = travel > 0 ? ((m.viewStartDoc - m.lo) / travel) * room : 0;
        pos = Math.min(Math.max(pos, 0), room);
        if (horizontal) {
          thumbEl.style.width = `${Math.round(thumbLen)}px`;
          thumbEl.style.transform = `translateX(${Math.round(pos)}px)`;
        } else {
          thumbEl.style.height = `${Math.round(thumbLen)}px`;
          thumbEl.style.transform = `translateY(${Math.round(pos)}px)`;
        }
        st.bars[axis] = { horizontal, travel, room, viewSize, pos };
      };
      syncBar('h');
      syncBar('v');

      // ---- rulers ----
      if (prefsRef.current.rulers) {
        const unitDef = UNIT_DEFS[prefsRef.current.units] || UNIT_DEFS.in;
        const dpr = window.devicePixelRatio || 1;
        const cursor = cursorRef.current;
        if (hRulerRef.current) {
          drawRulerStrip(hRulerRef.current, {
            horizontal: true,
            cssLen: hRulerRef.current.clientWidth,
            dpr, zoom,
            vptOffset: vpt[4],
            posterOrigin: br.left,
            unitDef,
            cursorPx: cursor ? cursor.x : null,
          });
        }
        if (vRulerRef.current) {
          drawRulerStrip(vRulerRef.current, {
            horizontal: false,
            cssLen: vRulerRef.current.clientHeight,
            dpr, zoom,
            vptOffset: vpt[5],
            posterOrigin: br.top,
            unitDef,
            cursorPx: cursor ? cursor.y : null,
          });
        }
      }

      // ---- ruler guides (RS2): DOM hairlines from workspace.data.guides.
      // Always re-read the model here — undo/redo/zoom/pan/changeSize
      // correctness rides this sync for free. A mid-gesture guide renders
      // at its draft position; everything else renders model truth.
      const layer = guidesLayerRef.current;
      if (layer) {
        const guideList = Array.isArray(ws.data?.guides) ? ws.data.guides : [];
        const g = st.gesture;
        const draftAxis = g && g.mode === 'create' && g.valid ? g.axis : '';
        const shape = guideList.map((x) => x.axis).join('') + (draftAxis ? `|${draftAxis}` : '');
        if (layer.dataset.shape !== shape) {
          layer.dataset.shape = shape;
          layer.textContent = '';
          const mk = (axis, idx) => {
            const el = document.createElement('div');
            el.className = `cce-guide cce-guide-${axis}`;
            if (idx == null) el.classList.add('is-draft');
            else el.dataset.guideIdx = String(idx);
            const line = document.createElement('span');
            line.className = 'cce-guide-line';
            el.appendChild(line);
            layer.appendChild(el);
          };
          guideList.forEach((gd, i) => mk(gd.axis, i));
          if (draftAxis) mk(draftAxis, null);
        }
        const dpr2 = window.devicePixelRatio || 1;
        const kids = layer.children;
        const place = (el, axis, pos, extra) => {
          const scr = axis === 'v'
            ? (br.left + pos) * zoom + vpt[4]
            : (br.top + pos) * zoom + vpt[5];
          const lim = axis === 'v' ? cw : ch;
          el.style.display = (scr < -8 || scr > lim + 8) ? 'none' : '';
          // device-snapped so the 1px line is crisp at DPR 1 and 2
          const snapped = Math.round(scr * dpr2) / dpr2 - GUIDE_HALF_SLAB;
          el.style.transform = axis === 'v' ? `translateX(${snapped}px)` : `translateY(${snapped}px)`;
          el.classList.toggle('is-dragging', !!(extra && extra.dragging));
          el.classList.toggle('is-removing', !!(extra && extra.removing));
        };
        guideList.forEach((gd, i) => {
          const el = kids[i];
          if (!el) return;
          const moving = g && g.mode === 'move' && g.index === i;
          place(el, gd.axis, moving ? g.pos : gd.pos, moving ? { dragging: true, removing: g.overRuler } : null);
        });
        if (draftAxis && kids[guideList.length]) {
          place(kids[guideList.length], draftAxis, g.pos, { dragging: true });
        }
      }
    });
  }, []);

  // Hook the SAME signals the patch system trusts: after:render with a
  // vpt/size/workspace change key (cheap prop reads, no per-frame redraw when
  // only objects move), plus a wrapper ResizeObserver for strip lengths.
  useEffect(() => {
    if (!fabricCanvas) return undefined;
    const st = stateRef.current;
    let lastVpt = '';
    let lastGeo = '';
    let lastGuidesArr = null; // identity compare — setGuides/undo/load swap the array
    const onAfterRender = () => {
      const v = fabricCanvas.viewportTransform;
      if (!v) return;
      const ws = fabricCanvas.getObjects?.().find((o) => o.name === 'clip');
      const vptKey = `${v[0]},${Math.round(v[4])},${Math.round(v[5])}`;
      const geoKey = `${fabricCanvas.width},${fabricCanvas.height},`
        + (ws ? `${ws.left},${ws.top},${ws.width * (ws.scaleX || 1)},${ws.height * (ws.scaleY || 1)}` : '');
      const guidesArr = ws?.data?.guides || null;
      if (vptKey === lastVpt && geoKey === lastGeo && guidesArr === lastGuidesArr) return;
      const vptMoved = lastVpt !== '' && vptKey !== lastVpt;
      lastVpt = vptKey;
      lastGeo = geoKey;
      lastGuidesArr = guidesArr;
      if (vptMoved) markActivity(); // wake the bars on real view motion
      scheduleSync();
    };
    fabricCanvas.on('after:render', onAfterRender);
    const ro = new ResizeObserver(() => scheduleSync());
    if (wrapRef.current) ro.observe(wrapRef.current);
    scheduleSync();
    return () => {
      fabricCanvas.off('after:render', onAfterRender);
      ro.disconnect();
      if (st.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
      if (st.idleTimer) { clearTimeout(st.idleTimer); st.idleTimer = 0; }
    };
  }, [fabricCanvas, scheduleSync, markActivity]);

  // Ruler toggle / unit change: redraw (the container's 22px reflow also
  // retriggers the hook's own ResizeObserver → refit or clamp — that's the
  // documented, acceptable SETTING reflow; drawers never come through here).
  useEffect(() => { scheduleSync(); }, [prefs, fabricCanvas, scheduleSync]);

  // ---- scrollbar interaction (house pan path only) ----
  const panDoc = useCallback((axis, dDoc) => {
    const ed = editorRef.current;
    if (!ed?.panBy || !dDoc) return;
    // viewStartDoc = -(posterStart·zoom + vpt) → moving the view +dDoc means
    // panning the scene by -dDoc screen px on that axis.
    if (axis === 'h') ed.panBy(-dDoc, 0);
    else ed.panBy(0, -dDoc);
  }, []);

  const onThumbPointerDown = (axis) => (e) => {
    const bar = stateRef.current.bars[axis];
    if (!bar || bar.room <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* no-op */ }
    stateRef.current.drag = { axis, last: bar.horizontal ? e.clientX : e.clientY };
    const barEl = axis === 'h' ? hBarRef.current : vBarRef.current;
    if (barEl) barEl.classList.add('is-drag');
    markActivity();
  };

  const onThumbPointerMove = (axis) => (e) => {
    const st = stateRef.current;
    if (!st.drag || st.drag.axis !== axis) return;
    const bar = st.bars[axis];
    if (!bar || bar.room <= 0) return;
    const cur = bar.horizontal ? e.clientX : e.clientY;
    const d = cur - st.drag.last;
    if (!d) return;
    st.drag.last = cur;
    // Live during drag: translation-only vpt moves are cheap; the fx patch
    // system re-bakes on its own 250ms debounce after the view settles.
    panDoc(axis, (d * bar.travel) / bar.room);
  };

  const endThumbDrag = (axis) => () => {
    const st = stateRef.current;
    if (!st.drag || st.drag.axis !== axis) return;
    st.drag = null;
    const barEl = axis === 'h' ? hBarRef.current : vBarRef.current;
    if (barEl) barEl.classList.remove('is-drag');
    markActivity(); // re-arm the idle fade
  };

  // Track click (not on the thumb): page-jump one viewport toward the click.
  const onTrackPointerDown = (axis) => (e) => {
    if (e.target !== e.currentTarget) return;
    const bar = stateRef.current.bars[axis];
    if (!bar) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const clickPos = bar.horizontal ? e.clientX - rect.left : e.clientY - rect.top;
    const dir = clickPos < bar.pos ? -1 : 1;
    panDoc(axis, dir * bar.viewSize * 0.9);
    markActivity();
  };

  // ---- ruler guides: create / move / remove gestures (RS2) ----
  // All gestures capture the pointer on the WRAPPER (stable across the
  // guide layer's rebuilds) and end through finishGesture — commit paths
  // write the model ONCE (one history entry); every cancel path writes
  // nothing and the next sync snaps the DOM back to model truth.

  /** client point → guide pos in canvas units from the poster corner
   *  (exact inverse of the render mapping — ruler agreement by shared law). */
  const clientToPos = useCallback((axis, clientX, clientY) => {
    const canvas = editorRef.current?.canvas;
    const cont = containerRef?.current;
    if (!canvas || !cont) return null;
    const vpt = canvas.viewportTransform;
    const ws = canvas.getObjects?.().find((o) => o.name === 'clip');
    if (!vpt || !ws) return null;
    const zoom = canvas.getZoom();
    let br;
    try { br = ws.getBoundingRect(true, true); } catch (_) { return null; }
    const r = cont.getBoundingClientRect();
    return axis === 'v'
      ? (clientX - r.left - vpt[4]) / zoom - br.left
      : (clientY - r.top - vpt[5]) / zoom - br.top;
  }, [containerRef]);

  /** Where is the pointer relative to the canvas area / the ruler gutters?
   *  Ruler zones only exist while the rulers pref is ON (rulers hidden =
   *  no create affordance and no drag-to-remove target). */
  const hitInfo = useCallback((clientX, clientY) => {
    const cont = containerRef?.current;
    if (!cont) return { insideCanvas: false, aboveCanvas: false, leftOfCanvas: false };
    const r = cont.getBoundingClientRect();
    const x = clientX - r.left;
    const y = clientY - r.top;
    const rulersOn = !!prefsRef.current.rulers;
    return {
      insideCanvas: x >= 0 && y >= 0 && x <= r.width && y <= r.height,
      aboveCanvas: rulersOn && y < 0,   // h-guide removal zone (source ruler side)
      leftOfCanvas: rulersOn && x < 0,  // v-guide removal zone
    };
  }, [containerRef]);

  const showLabel = useCallback((clientX, clientY, posCanvasUnits) => {
    const label = labelRef.current;
    const wrap = wrapRef.current;
    if (!label || !wrap) return;
    const r = wrap.getBoundingClientRect();
    label.textContent = fmtGuideLabel(posCanvasUnits, prefsRef.current.units);
    label.style.transform = `translate(${Math.round(clientX - r.left + 14)}px, ${Math.round(clientY - r.top + 14)}px)`;
    label.classList.add('is-on');
  }, []);
  const hideLabel = useCallback(() => {
    labelRef.current?.classList.remove('is-on');
  }, []);

  const finishGesture = useCallback((commit) => {
    const st = stateRef.current;
    const g = st.gesture;
    if (!g) return;
    st.gesture = null;
    if (escHandlerRef.current) {
      window.removeEventListener('keydown', escHandlerRef.current, true);
      escHandlerRef.current = null;
    }
    if (g.pointerId != null && wrapRef.current) {
      try { wrapRef.current.releasePointerCapture(g.pointerId); } catch (_) { /* already released */ }
    }
    hideLabel();
    const ed = editorRef.current;
    if (commit && ed?.setGuides) {
      const guides = ed.getGuides ? ed.getGuides() : [];
      if (g.mode === 'create') {
        // release on the canvas commits; release back over a ruler cancels
        if (g.valid) ed.setGuides([...guides, { axis: g.axis, pos: g.pos }]);
      } else if (g.mode === 'move' && guides[g.index] && guides[g.index].axis === g.axis) {
        // (axis identity guard: an undo mid-drag may have reshaped the list)
        if (g.overRuler) {
          // dropped back on its source ruler = remove
          ed.setGuides(guides.filter((_, i) => i !== g.index));
        } else if (g.valid && Math.abs(g.pos - g.origPos) > 0.005) {
          ed.setGuides(guides.map((x, i) => (i === g.index ? { axis: g.axis, pos: g.pos } : x)));
        }
      }
    }
    scheduleSync();
  }, [hideLabel, scheduleSync]);

  const armEsc = useCallback(() => {
    const h = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      // keep the editor's own Escape (discard selection) out of this one
      e.stopImmediatePropagation();
      finishGesture(false);
    };
    escHandlerRef.current = h;
    window.addEventListener('keydown', h, true);
  }, [finishGesture]);

  const beginGesture = useCallback((gesture, e) => {
    try { wrapRef.current?.setPointerCapture(e.pointerId); } catch (_) { /* touch oddities */ }
    stateRef.current.gesture = { ...gesture, pointerId: e.pointerId };
    armEsc();
    scheduleSync();
  }, [armEsc, scheduleSync]);

  // Create: pointerdown on a ruler strip, drag toward the canvas. The top
  // strip spawns HORIZONTAL guides, the left strip VERTICAL (the house/PS
  // convention). Live draft follows the cursor; release over the canvas
  // commits ONE entry; release back over the gutter (or Esc) cancels.
  const onRulerPointerDown = (axis) => (e) => {
    if (e.button !== 0) return;
    const ed = editorRef.current;
    if (!ed?.setGuides || !ed.canvas) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = clientToPos(axis, e.clientX, e.clientY);
    beginGesture({
      mode: 'create', axis, index: -1,
      pos: pos == null ? 0 : pos, origPos: null,
      valid: false, overRuler: false,
    }, e);
  };

  // Move (and remove): pointerdown on an existing guide's hit slab —
  // delegated on the layer so rebuilt children need no rewiring.
  const onGuidePointerDown = (e) => {
    if (e.button !== 0) return;
    const hitEl = e.target?.closest?.('[data-guide-idx]');
    if (!hitEl) return;
    const index = parseInt(hitEl.dataset.guideIdx, 10);
    const guides = editorRef.current?.getGuides ? editorRef.current.getGuides() : [];
    const guide = guides[index];
    if (!guide) return;
    e.preventDefault();
    e.stopPropagation();
    beginGesture({
      mode: 'move', axis: guide.axis, index,
      pos: guide.pos, origPos: guide.pos,
      valid: true, overRuler: false,
    }, e);
    showLabel(e.clientX, e.clientY, guide.pos);
  };

  const onWrapPointerUp = (e) => {
    const g = stateRef.current.gesture;
    if (g && e.pointerId === g.pointerId) finishGesture(true);
  };
  const onWrapPointerCancel = () => {
    if (stateRef.current.gesture) finishGesture(false);
  };

  // Wrapper pointermove: guide gesture updates + guide hover labels +
  // the ruler cursor marker (all cheap; redraws ride the one rAF sync).
  const onWrapPointerMove = (e) => {
    const st = stateRef.current;
    const g = st.gesture;
    if (g && e.pointerId === g.pointerId) {
      const pos = clientToPos(g.axis, e.clientX, e.clientY);
      const hit = hitInfo(e.clientX, e.clientY);
      if (pos != null) g.pos = pos;
      g.valid = hit.insideCanvas;
      g.overRuler = g.mode === 'move'
        ? (g.axis === 'h' ? hit.aboveCanvas : hit.leftOfCanvas)
        : false;
      if (g.valid && !g.overRuler) showLabel(e.clientX, e.clientY, g.pos);
      else hideLabel(); // over the gutter: the dimmed line telegraphs removal
      scheduleSync();
    } else if (!g) {
      // hover a guide → its position pill (works with rulers hidden too —
      // guides are canvas annotations, not ruler chrome)
      const hitEl = e.target?.closest?.('[data-guide-idx]');
      if (hitEl) {
        const guides = editorRef.current?.getGuides ? editorRef.current.getGuides() : [];
        const gd = guides[parseInt(hitEl.dataset.guideIdx, 10)];
        if (gd) showLabel(e.clientX, e.clientY, gd.pos);
        else hideLabel();
      } else {
        hideLabel();
      }
    }
    // cursor position marker on the rulers
    if (!prefsRef.current.rulers) return;
    const cont = containerRef?.current;
    if (!cont) return;
    const r = cont.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height;
    const next = inside ? { x, y } : null;
    if (!next && !cursorRef.current) return;
    cursorRef.current = next;
    scheduleSync();
  };
  const onWrapPointerLeave = () => {
    if (!stateRef.current.gesture) hideLabel();
    if (!cursorRef.current) return;
    cursorRef.current = null;
    scheduleSync();
  };

  // Snap engagement flash: the engine (aligningGuidelines) fires
  // cce-guide-snap per moving tick while an object is held on a guide —
  // brighten that guide, let it cool SNAP_FLASH_MS after the last tick.
  useEffect(() => {
    const onSnap = (e) => {
      const layer = guidesLayerRef.current;
      if (!layer) return;
      const idx = e?.detail?.index;
      const el = layer.querySelector(`[data-guide-idx="${idx}"]`);
      if (el) el.classList.add('is-snapped');
      const st = stateRef.current;
      if (st.snapTimer) clearTimeout(st.snapTimer);
      st.snapTimer = setTimeout(() => {
        st.snapTimer = 0;
        layer.querySelectorAll('.is-snapped').forEach((n) => n.classList.remove('is-snapped'));
      }, SNAP_FLASH_MS);
    };
    window.addEventListener('cce-guide-snap', onSnap);
    return () => {
      window.removeEventListener('cce-guide-snap', onSnap);
      const st = stateRef.current;
      if (st.snapTimer) { clearTimeout(st.snapTimer); st.snapTimer = 0; }
      if (escHandlerRef.current) {
        window.removeEventListener('keydown', escHandlerRef.current, true);
        escHandlerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className={`cce-canvas-viewport${prefs.rulers ? ' has-rulers' : ''}`}
      onPointerMove={onWrapPointerMove}
      onPointerLeave={onWrapPointerLeave}
      onPointerUp={onWrapPointerUp}
      onPointerCancel={onWrapPointerCancel}
    >
      <div
        className="cce-canvas-container"
        ref={containerRef}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {children}
      </div>

      {/* guide hairlines (DOM, never fabric/exported); layer is inert,
          children carry the 7px hit slabs. Rendered with rulers on OR off —
          guides are canvas annotations, only their CREATE affordance (the
          strips below) is ruler chrome. */}
      <div
        ref={guidesLayerRef}
        className="cce-guides-layer"
        onPointerDown={onGuidePointerDown}
        aria-hidden="true"
      />
      <div ref={labelRef} className="cce-guide-label" aria-hidden="true" />

      {prefs.rulers && (
        <>
          <canvas
            ref={hRulerRef}
            className="cce-ruler cce-ruler-h"
            title="Drag down to add a guide"
            onPointerDown={onRulerPointerDown('h')}
            aria-hidden="true"
          />
          <canvas
            ref={vRulerRef}
            className="cce-ruler cce-ruler-v"
            title="Drag right to add a guide"
            onPointerDown={onRulerPointerDown('v')}
            aria-hidden="true"
          />
          <div className="cce-ruler-corner" aria-hidden="true" />
        </>
      )}

      <div
        ref={hBarRef}
        className="cce-scrollbar cce-scrollbar-h is-hidden"
        onPointerDown={onTrackPointerDown('h')}
        role="presentation"
      >
        <div
          ref={hThumbRef}
          className="cce-scrollbar-thumb"
          onPointerDown={onThumbPointerDown('h')}
          onPointerMove={onThumbPointerMove('h')}
          onPointerUp={endThumbDrag('h')}
          onLostPointerCapture={endThumbDrag('h')}
        />
      </div>
      <div
        ref={vBarRef}
        className="cce-scrollbar cce-scrollbar-v is-hidden"
        onPointerDown={onTrackPointerDown('v')}
        role="presentation"
      >
        <div
          ref={vThumbRef}
          className="cce-scrollbar-thumb"
          onPointerDown={onThumbPointerDown('v')}
          onPointerMove={onThumbPointerMove('v')}
          onPointerUp={endThumbDrag('v')}
          onLostPointerCapture={endThumbDrag('v')}
        />
      </div>
    </div>
  );
}
